import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../api/supabase'
import CountrySelect from '../../components/CountrySelect'
import { useAuth } from '../../contexts/AuthContext'
import { hasAnyRole } from '../../lib/roles'
import ExistingCompaniesNotice from '../../components/ExistingCompaniesNotice'
import { findExistingCompanies, type ExistingCompany } from '../../lib/existingCompanies'
import { countryFieldsForSave, countryForForm, countryNotValidMessage } from '../../lib/customerCountry'

interface Customer {
  id?: string
  code: string
  name: string
  country?: string
  contact_name?: string
  contact_email?: string
  contact_phone?: string
  payment_terms?: string
  currency: string
  customer_type?: string
  is_active: boolean
  address_types?: AddressType[]
}

interface AddressType {
  id: string
  code: string
  description: string
  sort_order: number
}

const CURRENCIES = ['GBP', 'USD', 'EUR', 'CNY', 'HKD', 'JPY', 'AUD', 'CAD']

const empty: Customer = {
  code: '', name: '', country: '', contact_name: '',
  contact_email: '', contact_phone: '', payment_terms: '', currency: 'GBP',
  customer_type: '', is_active: true,
}

const inputCls = 'w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white'

const TYPE_CHIP_COLORS = [
  'bg-blue-100 text-blue-700', 'bg-green-100 text-green-700', 'bg-yellow-100 text-yellow-700',
  'bg-purple-100 text-purple-700', 'bg-orange-100 text-orange-700', 'bg-pink-100 text-pink-700',
  'bg-indigo-100 text-indigo-700', 'bg-teal-100 text-teal-700', 'bg-red-100 text-red-700',
  'bg-cyan-100 text-cyan-700', 'bg-gray-100 text-gray-700',
]

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  )
}

function Modal({ title, onClose, onSubmit, saving, error, children }: {
  title: string; onClose: () => void; onSubmit: (e: React.FormEvent) => void
  saving: boolean; error: string | null; children: React.ReactNode
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={onSubmit} className="p-6 space-y-4">
          {error && <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-sm">{error}</div>}
          {children}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">Cancel</button>
            <button type="submit" disabled={saving} className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default function DataCustomers() {
  const { userRoles } = useAuth()
  const canEdit = hasAnyRole(userRoles, ['ADMIN', 'OPERATIONS'])

  const [rows, setRows] = useState<Customer[]>([])
  const [allTypes, setAllTypes] = useState<AddressType[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('active')
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState<Customer>(empty)
  const [selectedTypeIds, setSelectedTypeIds] = useState<Set<string>>(new Set())
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // A NEW company is only created after the database has been asked whether one like it already exists.
  const [existing, setExisting] = useState<ExistingCompany[] | null>(null)
  const [skipExistingCheck, setSkipExistingCheck] = useState(false)
  useEffect(() => { setSkipExistingCheck(false); setExisting(null) }, [form.name, form.code])

  useEffect(() => {
    supabase.from('address_types').select('id,code,description,sort_order').eq('is_active', true).order('sort_order')
      .then(({ data }) => setAllTypes(data || []))
  }, [])

  const typeColorMap = Object.fromEntries(
    allTypes.map((t, i) => [t.id, TYPE_CHIP_COLORS[i % TYPE_CHIP_COLORS.length]])
  )

  const load = useCallback(async () => {
    setLoading(true)
    let q = supabase.from('customers')
      .select('id,code,name,country,contact_email,payment_terms,currency,is_active,customer_address_types(address_type_id,address_types(id,code,description))')
      .order('name')
    if (search) q = q.or(`name.ilike.%${search}%,code.ilike.%${search}%,contact_email.ilike.%${search}%`)
    if (filterActive === 'active') q = q.eq('is_active', true)
    if (filterActive === 'inactive') q = q.eq('is_active', false)
    const { data } = await q.limit(200)
    const mapped = (data || []).map((c: any) => ({
      ...c,
      address_types: (c.customer_address_types || []).map((jt: any) => jt.address_types).filter(Boolean),
    }))
    setRows(mapped)
    setLoading(false)
  }, [search, filterActive])

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t) }, [load])

  const openAdd = () => {
    setForm(empty); setSelectedTypeIds(new Set()); setError(null); setModal(true)
  }

  const openEdit = async (r: Customer) => {
    const [{ data: full }, { data: types }] = await Promise.all([
      supabase.from('customers').select('*').eq('id', r.id!).single(),
      supabase.from('customer_address_types').select('address_type_id').eq('customer_id', r.id!),
    ])
    setForm({ ...(full || r), country: countryForForm((full || r).country) })
    setSelectedTypeIds(new Set((types || []).map((t: any) => t.address_type_id)))
    setError(null)
    setModal(true)
  }

  const set = (k: keyof Customer, v: unknown) => setForm(p => ({ ...p, [k]: v }))

  const toggleType = (id: string) =>
    setSelectedTypeIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.code.trim() || !form.name.trim()) { setError('Code and Name are required.'); return }
    // Country: same rule as the Companies form (lib/customerCountry.ts): both columns from one picker; not-a-country text is refused, never copied into country_code.
    const countryFields = countryFieldsForSave(form.country)
    if (!countryFields.ok) { setError(countryNotValidMessage(countryFields.text)); return }
    setSaving(true); setError(null)
    try {
      if (!form.id && !skipExistingCheck) {
        const found = await findExistingCompanies(form.name, form.code)
        if (found.length > 0) { setExisting(found); setSaving(false); return }
      }
      // address omitted -- deprecated, superseded by customer_addresses (see Customers/Detail.tsx)
      const payload = {
        code: form.code, name: form.name, country: countryFields.country, country_code: countryFields.country_code,
        contact_name: form.contact_name, contact_email: form.contact_email,
        contact_phone: form.contact_phone, payment_terms: form.payment_terms,
        currency: form.currency, is_active: form.is_active,
      }
      let customerId = form.id
      if (customerId) {
        const { error: err } = await supabase.from('customers').update(payload).eq('id', customerId)
        if (err) throw err
      } else {
        const { data, error: err } = await supabase.from('customers').insert(payload).select('id').single()
        if (err) throw err
        customerId = data.id
      }

      // Sync junction table
      await supabase.from('customer_address_types').delete().eq('customer_id', customerId!)
      if (selectedTypeIds.size > 0) {
        await supabase.from('customer_address_types').insert(
          Array.from(selectedTypeIds).map(tid => ({ customer_id: customerId!, address_type_id: tid }))
        )
      }

      setModal(false); load()
    } catch (err: any) { setError(err?.message || err?.error_description || String(err) || 'Unknown error') }
    finally { setSaving(false) }
  }

  const toggleActive = async (r: Customer) => {
    await supabase.from('customers').update({ is_active: !r.is_active }).eq('id', r.id!)
    load()
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <Link to="/data" className="text-sm text-blue-600 dark:text-blue-400 hover:underline">← Data Management</Link>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mt-1">Address Book</h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-0.5">Companies — source data for job creation and invoicing</p>
        </div>

        <div className="flex flex-wrap gap-3 mb-4 items-end">
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, code or email…"
            className="flex-1 min-w-[220px] border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
          <select value={filterActive} onChange={e => setFilterActive(e.target.value as typeof filterActive)}
            className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white">
            <option value="all">All</option>
            <option value="active">Active only</option>
            <option value="inactive">Inactive only</option>
          </select>
          {canEdit && (
            <button onClick={openAdd} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
              + Add Company
            </button>
          )}
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
          {loading ? (
            <div className="py-12 text-center text-gray-400">Loading…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                  <tr>
                    {['Code', 'Name', 'Roles', 'Country', 'Contact Email', 'Payment Terms', 'Currency', 'Status', ...(canEdit ? ['Actions'] : [])].map(h => (
                      <th key={h} className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-300 text-xs uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {rows.map(r => (
                    <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3 font-mono font-bold text-gray-900 dark:text-white whitespace-nowrap">{r.code}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-200 max-w-[200px] truncate">
                        {r.id ? (
                          <Link to={`/customers/${r.id}`} className="hover:text-blue-600 hover:underline">{r.name}</Link>
                        ) : r.name}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {(r.address_types || []).length === 0 ? (
                            <span className="text-xs text-gray-400">—</span>
                          ) : (
                            (r.address_types || []).map((t: AddressType) => (
                              <span key={t.id} className={`px-1.5 py-0.5 rounded text-xs font-semibold whitespace-nowrap ${typeColorMap[t.id] || 'bg-gray-100 text-gray-600'}`}>
                                {t.description}
                              </span>
                            ))
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{r.country || '—'}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400 max-w-[180px] truncate">{r.contact_email || '—'}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{r.payment_terms || '—'}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 rounded text-xs font-mono font-semibold bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                          {r.currency || 'GBP'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${r.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {r.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      {canEdit && (
                        <td className="px-4 py-3 space-x-3 whitespace-nowrap">
                          <button onClick={() => openEdit(r)} className="text-blue-600 hover:underline text-xs font-medium">Edit</button>
                          <button onClick={() => toggleActive(r)} className={`text-xs font-medium ${r.is_active ? 'text-red-500 hover:underline' : 'text-green-600 hover:underline'}`}>
                            {r.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr><td colSpan={canEdit ? 9 : 8} className="px-4 py-8 text-center text-gray-400">No companies found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="mt-2 text-xs text-gray-400">{rows.length} record{rows.length !== 1 ? 's' : ''}</div>
      </div>

      {modal && (
        <Modal title={form.id ? 'Edit Company' : 'Add Company'} onClose={() => setModal(false)} onSubmit={handleSubmit} saving={saving} error={error}>
          {existing && !form.id && (
            <ExistingCompaniesNotice
              typedName={form.name.trim()}
              matches={existing}
              busy={saving}
              onCreateAnyway={() => { setExisting(null); setSkipExistingCheck(true); setError('Confirmed as a different company — press Save again to create it.') }}
            />
          )}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Code" required>
              <input className={inputCls} value={form.code} onChange={e => set('code', e.target.value.toUpperCase())} placeholder="e.g. ACME001" maxLength={20} />
            </Field>
            <Field label="Currency">
              <select className={inputCls} value={form.currency} onChange={e => set('currency', e.target.value)}>
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Name" required>
            <input className={inputCls} value={form.name} onChange={e => set('name', e.target.value)} placeholder="Acme Corporation Ltd" />
          </Field>

          {/* Company Roles */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Company Roles</label>
            {allTypes.length === 0 ? (
              <p className="text-xs text-gray-400">Loading…</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {allTypes.map(at => {
                  const checked = selectedTypeIds.has(at.id)
                  return (
                    <button
                      key={at.id}
                      type="button"
                      onClick={() => toggleType(at.id)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                        checked
                          ? 'bg-blue-600 border-blue-600 text-white'
                          : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-600 dark:text-gray-300 hover:border-blue-400'
                      }`}
                    >
                      {at.description}
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Country">
              <CountrySelect value={form.country} onChange={code => set('country', code)} invalid={!countryFieldsForSave(form.country).ok} />
              {!countryFieldsForSave(form.country).ok && <p className="mt-1 text-xs text-red-600">Saved value &quot;{form.country}&quot; is not a country: choose one from the list.</p>}
            </Field>
            <Field label="Payment Terms">
              <input className={inputCls} value={form.payment_terms || ''} onChange={e => set('payment_terms', e.target.value)} placeholder="30DAY" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Contact Name">
              <input className={inputCls} value={form.contact_name || ''} onChange={e => set('contact_name', e.target.value)} placeholder="John Smith" />
            </Field>
            <Field label="Contact Phone">
              <input className={inputCls} value={form.contact_phone || ''} onChange={e => set('contact_phone', e.target.value)} placeholder="+44 20 1234 5678" />
            </Field>
          </div>
          <Field label="Contact Email">
            <input type="email" className={inputCls} value={form.contact_email || ''} onChange={e => set('contact_email', e.target.value)} placeholder="john@acme.com" />
          </Field>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} className="h-4 w-4 text-blue-600 rounded" />
            <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
          </label>
        </Modal>
      )}
    </div>
  )
}
